import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useMemo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { NotificationsService } from '../../../js/api';
import { QUEUE_NOTIFICATIONS_QUERY_KEY } from '../constants/queryKeys';
import { WorkflowScopes } from '../constants/workflows';
import { useCodeDefinitionsByCategory } from '../hooks/useCodeDefinitions';
import { useIsMobile } from '../hooks/useIsMobile';
import { DropdownSelect } from './dropdown-select';
import { Field, Label } from './fieldset';
import { Tabs, TabsList, TabsButton } from './tabs';

export interface QueueListProps {
  /**
   * Code def category name used to fetch the list of queue names from Code Defs
   * (e.g. "HealthcareEntityConnectionQueueNames")
   */
  categoryName: string;
  /**
   * Workflow scope for the notifications API that will return notification counts per queue.
   * Must be a valid WorkflowScopes enum value.
   */
  notificationScope?: WorkflowScopes;
  /**
   * Callback function called when a user changes the queue.
   * The parent should use this to update query params or state.
   */
  onQueueChange: (queueCode: string) => void;
  /**
   * Currently selected queue code. If not provided, will be determined by priority logic.
   */
  selectedQueue?: string;
  /**
   * Whether to use priority queue logic for determining initial focus.
   * When true, focuses on first queue with notifications > 0 from priority list.
   * When false, focuses on first queue in the list.
   * Defaults to true.
   */
  priorityQueues?: boolean;
  /**
   * List of queue codes in priority order for determining initial focus.
   * Only used when priorityQueues is true.
   * If empty, uses the full list of queue names from codedef in order.
   */
  priorityQueueList?: string[];
  /**
   * Optional className for the container
   */
  className?: string;
}

/**
 * QueueList Component
 *
 * A reusable component for displaying and managing queue-based navigation tabs.
 * Fetches queue names from Code Definitions and displays them as tabs with optional
 * notification badges.
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useSearchParams();
 * const selectedQueue = searchParams.get('queue');
 *
 * <QueueList
 *   categoryName="HealthcareEntityQueueNames"
 *   notificationScope={WorkflowScopes.ConnectedFacility}
 *   selectedQueue={selectedQueue || undefined}
 *   priorityQueues={true}
 *   priorityQueueList={['urgent', 'pending']}
 *   onQueueChange={(queueCode) => {
 *     const newParams = new URLSearchParams(searchParams);
 *     if (queueCode) {
 *       newParams.set('queue', queueCode);
 *     } else {
 *       newParams.delete('queue');
 *     }
 *     setSearchParams(newParams, { replace: true });
 *   }}
 * />
 * ```
 *
 * @remarks
 * **Cache Invalidation**: To invalidate the notification cache for a specific scope,
 * use the query key pattern: `[QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.<scope>]`
 *
 * @example
 * ```tsx
 * import { useQueryClient } from '@tanstack/react-query';
 * import { WorkflowScopes } from '../constants/workflows';
 * import { QUEUE_NOTIFICATIONS_QUERY_KEY } from '../constants/queryKeys';
 *
 * const queryClient = useQueryClient();
 *
 * // Invalidate notifications for ConnectedFacility scope
 * queryClient.invalidateQueries({
 *   queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, WorkflowScopes.ConnectedFacility]
 * });
 * ```
 */
export const QueueList: React.FC<QueueListProps> = ({
  categoryName,
  notificationScope,
  onQueueChange,
  selectedQueue,
  priorityQueues = true,
  priorityQueueList = [],
  className,
}) => {
  const isMobile = useIsMobile();
  // Fetch queue names from Code Defs (ordered by sequence)
  const { data: queueDefinitions, isLoading: isLoadingQueues } =
    useCodeDefinitionsByCategory(categoryName);

  const activeQueues = useMemo(() => {
    if (!queueDefinitions) return [];
    return queueDefinitions.filter((def) => def.is_active !== false);
  }, [queueDefinitions]);

  // Get valid queue codes for validation (derived from active queues)
  const validQueueCodes = useMemo(() => {
    return new Set(activeQueues.map((queue) => queue.code));
  }, [activeQueues]);

  // Fetch notification counts from API
  // Only fetches if notificationScope is provided
  // API returns: { "queue_code": { "count": number }, ... }
  const {
    data: notificationCounts = {} as Record<string, { count: number }>,
    isFetched: isNotificationsFetched,
  } = useQuery({
    queryKey: [QUEUE_NOTIFICATIONS_QUERY_KEY, notificationScope],
    queryFn: async () => {
      const response = await NotificationsService.notificationsBadgesScopeRetrieve({
        scope: notificationScope!,
      });
      // API returns a record mapping queue codes to objects with count property
      return (response as Record<string, { count: number }>) || {};
    },
    enabled: !!notificationScope,
    staleTime: 30 * 1000, // 30 seconds - notifications may change frequently
  });

  // Determine the initial tab to focus based on priority logic
  const determineInitialTab = useMemo(() => {
    if (activeQueues.length === 0) return null;

    if (!priorityQueues) {
      return activeQueues[0]?.code || null;
    }

    // If we're using priority logic and a notification scope is provided,
    // wait until notifications have been fetched so initial selection can
    // correctly prioritize queues with counts.
    if (notificationScope && !isNotificationsFetched) {
      return null;
    }

    const queuesToCheck =
      priorityQueueList.length > 0
        ? priorityQueueList.filter((code) => validQueueCodes.has(code))
        : activeQueues.map((queue) => queue.code);

    for (const queueCode of queuesToCheck) {
      const queueNotification = notificationCounts[queueCode];
      const count = queueNotification?.count || 0;
      if (count > 0) {
        return queueCode;
      }
    }

    return activeQueues[0]?.code || null;
  }, [
    activeQueues,
    priorityQueues,
    priorityQueueList,
    validQueueCodes,
    notificationCounts,
    notificationScope,
    isNotificationsFetched,
  ]);

  const validatedSelectedQueue = useMemo(() => {
    if (selectedQueue && validQueueCodes.has(selectedQueue)) {
      return selectedQueue;
    }

    if (activeQueues.length > 0 && determineInitialTab) {
      return determineInitialTab;
    }

    return selectedQueue || null;
  }, [selectedQueue, validQueueCodes, activeQueues, determineInitialTab]);

  // When component mounts or queues load, if no selectedQueue is provided,
  // set the initial tab by calling onQueueChange
  useEffect(() => {
    if (
      !isLoadingQueues &&
      activeQueues.length > 0 &&
      determineInitialTab &&
      // Ensure notifications are fetched before setting initial when a scope is used
      (!notificationScope || isNotificationsFetched) &&
      (!selectedQueue || !validQueueCodes.has(selectedQueue))
    ) {
      onQueueChange(determineInitialTab);
    }
  }, [
    isLoadingQueues,
    activeQueues,
    determineInitialTab,
    selectedQueue,
    validQueueCodes,
    onQueueChange,
    notificationScope,
    isNotificationsFetched,
  ]);

  const handleTabChange = (queueCode: string) => {
    onQueueChange(queueCode);
  };

  // Build options for mobile Select (prefilter to only active queues) and include counts in labels
  const mobileSelectOptions = useMemo(
    () =>
      activeQueues.map((def) => {
        const count = notificationCounts[def.code]?.count || 0;
        const label = count > 0 ? `${def.title} (${count})` : def.title;
        return { value: def.code, label };
      }),
    [activeQueues, notificationCounts]
  );

  if (isLoadingQueues) {
    return null;
  }

  if (activeQueues.length === 0) {
    return null;
  }

  if (!validatedSelectedQueue) {
    return null;
  }

  if (isMobile) {
    const selectId = 'queue-list-quick-filter';
    return (
      <div className={twMerge(clsx('tw:pb-2', className))}>
        <Field>
          <Label htmlFor={selectId} className="tw:pl-1">
            Select View
          </Label>
          <DropdownSelect
            inputId={selectId}
            aria-label="Select View"
            allOptions={mobileSelectOptions}
            value={validatedSelectedQueue}
            isSearchable={false}
            isClearable={false}
            onChange={(opt: any) => {
              if (opt && !Array.isArray(opt)) {
                onQueueChange(opt.value);
              }
            }}
          />
        </Field>
      </div>
    );
  }

  return (
    <div className={className}>
      <Tabs activeTab={validatedSelectedQueue} onChange={handleTabChange}>
        <TabsList>
          {activeQueues.map((queue) => {
            const queueNotification = notificationCounts[queue.code];
            const notificationCount = queueNotification?.count || 0;
            const showBadge = notificationCount > 0;

            return (
              <TabsButton
                key={queue.code}
                tab={queue.code}
                badge={showBadge ? notificationCount : undefined}
                dataTestId={`queue-tab-${queue.code}`}
              >
                {queue.title}
              </TabsButton>
            );
          })}
        </TabsList>
      </Tabs>
    </div>
  );
};
